import {
  PORTAL_PRESETS
} from './portal-presets.js'
import {
  getMissionAltitudeContextFor3d,
  syncPointAltitudeFields
} from './portal-altitude-utils.js'
import {
  startPortalLoadingTask
} from './portal-loading-widget.js'

const LOCAL_DEM_PROVIDER_KEY = 'localDEM'
const LOCAL_TERRAIN_STATE_KEY = 'PORTAL_LOCAL_TERRAIN_STATE'

const isHeightToolDebugEnabled = function () {
  return typeof window !== 'undefined' && window.PORTAL_DEBUG_HEIGHT_TOOL === true
}

const logHeightToolDebug = function (channel, payload) {
  if (!isHeightToolDebugEnabled()) return
  if (typeof console === 'undefined' || typeof console.log !== 'function') return
  const messagePayload = {
    channel: String(channel || '').trim(),
    ...(payload && typeof payload === 'object' ? payload : {})
  }
  try {
    console.log(`HEIGHT_TOOL_BATCH_DEBUG ${JSON.stringify(messagePayload)}`)
  } catch (err) {
    console.log('HEIGHT_TOOL_BATCH_DEBUG {"channel":"debug_log_stringify_failed"}')
  }
}

const DEM_BATCH_POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]

const DEM_BATCH_POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]

const DEM_BATCH_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]

const buildDemBatchAltitudeSyncContext = function (missionState, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  return {
    importMissionState: missionState || null,
    DEM_PROVIDER_PRIORITY: Array.isArray(opts.DEM_PROVIDER_PRIORITY) ? opts.DEM_PROVIDER_PRIORITY : [],
    ALTITUDE_RULES: PORTAL_PRESETS.altitudeRules,
    POINT_HEIGHT_PATH_ALIASES: DEM_BATCH_POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: DEM_BATCH_POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: DEM_BATCH_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: 'project.height',
    fnIsDemProviderEnabled: typeof opts.fnIsDemProviderEnabled === 'function' ? opts.fnIsDemProviderEnabled : function () { return true },
    fnSetByPath: typeof opts.fnSetByPath === 'function' ? opts.fnSetByPath : function () {},
    fnUpdatePointXmlByPath: typeof opts.fnUpdatePointXmlByPath === 'function' ? opts.fnUpdatePointXmlByPath : function () {},
    fnTemplateSupportsPointCoordinatesField: typeof opts.fnTemplateSupportsPointCoordinatesField === 'function' ? opts.fnTemplateSupportsPointCoordinatesField : function () { return false },
    fnBuildPointCoordinatesText: typeof opts.fnBuildPointCoordinatesText === 'function' ? opts.fnBuildPointCoordinatesText : function () { return '' }
  }
}

const resolvePointLatLng = function (pt) {
  const lat = parseFloat(
    (Array.isArray(pt) ? pt[0] : undefined) ??
    (pt && pt.latlng && pt.latlng[0]) ??
    (pt && pt.lat) ??
    (pt && pt.latitude)
  )
  const lng = parseFloat(
    (Array.isArray(pt) ? pt[1] : undefined) ??
    (pt && pt.latlng && pt.latlng[1]) ??
    (pt && pt.lng) ??
    (pt && pt.longitude)
  )
  return { lat: lat, lng: lng }
}

const resolveLocalFallbackProviderKey = function (options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const explicitProviderKey = String(opts.localFallbackProviderKey || '').trim()
  const fnIsDemProviderEnabled = (typeof opts.fnIsDemProviderEnabled === 'function')
    ? opts.fnIsDemProviderEnabled
    : function () { return true }
  if (explicitProviderKey && explicitProviderKey !== LOCAL_DEM_PROVIDER_KEY && fnIsDemProviderEnabled(explicitProviderKey)) {
    return explicitProviderKey
  }

  const priority = Array.isArray(opts.DEM_PROVIDER_PRIORITY) ? opts.DEM_PROVIDER_PRIORITY : []
  for (let idx = 0; idx < priority.length; idx++) {
    const providerKey = String(priority[idx] || '').trim()
    if (!providerKey || providerKey === LOCAL_DEM_PROVIDER_KEY) continue
    if (fnIsDemProviderEnabled(providerKey)) return providerKey
  }

  if (typeof window !== 'undefined' && typeof window.PORTAL_BUILD_MAP_CURSOR_ELEVATION_TRY_ORDER === 'function') {
    const candidates = window.PORTAL_BUILD_MAP_CURSOR_ELEVATION_TRY_ORDER('')
    for (let idx = 0; idx < candidates.length; idx++) {
      const providerKey = String(candidates[idx] || '').trim()
      if (!providerKey || providerKey === LOCAL_DEM_PROVIDER_KEY) continue
      if (fnIsDemProviderEnabled(providerKey)) return providerKey
    }
  }

  return 'gDEM'
}

const fetchSurfaceElevationByProvider = async function (providerKey, batchPoints, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const includeSourceProvider = opts.includeSourceProvider === true
  const fnIsDemProviderEnabled = (typeof opts.fnIsDemProviderEnabled === 'function')
    ? opts.fnIsDemProviderEnabled
    : function () { return true }
  const fnT = (typeof opts.fnT === 'function')
    ? opts.fnT
    : function (key) { return String(key || '') }
  const fetchImpl = (typeof opts.fetchImpl === 'function')
    ? opts.fetchImpl
    : ((typeof fetch === 'function') ? fetch : null)
  const fnGetLocalTerrainState = (typeof opts.fnGetLocalTerrainState === 'function')
    ? opts.fnGetLocalTerrainState
    : function () {
        if (typeof window === 'undefined') return null
        return window[LOCAL_TERRAIN_STATE_KEY] || null
      }
  const localFallbackProviderKey = resolveLocalFallbackProviderKey({
    localFallbackProviderKey: opts.localFallbackProviderKey,
    DEM_PROVIDER_PRIORITY: opts.DEM_PROVIDER_PRIORITY,
    fnIsDemProviderEnabled: fnIsDemProviderEnabled
  })
  const normalizedProviderKey = String(providerKey || '').trim()

  const pointsPayload = batchPoints.map(resolvePointLatLng)
  const fnToFiniteElevationOrNull = function (value) {
    if (value === null || value === undefined) return null
    const elevation = Number(value)
    return Number.isFinite(elevation) ? elevation : null
  }
  const fnBuildElevationResultRows = function (elevations, providers, fallbackProviderKey) {
    return pointsPayload.map(function (_pt, idx) {
      const elevation = fnToFiniteElevationOrNull(elevations[idx])
      if (elevation === null) {
        return includeSourceProvider
          ? { elevation: null, provider: '' }
          : null
      }
      const sourceProviderKey = String((providers && providers[idx]) || fallbackProviderKey || '').trim()
      return includeSourceProvider
        ? { elevation: elevation, provider: sourceProviderKey }
        : elevation
    })
  }

  if (normalizedProviderKey === LOCAL_DEM_PROVIDER_KEY) {
    const localTerrainState = fnGetLocalTerrainState()
    const fnHasLocalTerrainSampler = function (state) {
      return !!(
        state &&
        state.active === true &&
        (
          typeof state.sampleElevation === 'function' ||
          typeof state.sampleElevationAsync === 'function' ||
          typeof state.sampleElevations === 'function'
        )
      )
    }
    const fnIsPointInsideLocalTerrain = function (state, point) {
      const lat = Number(point && point.lat)
      const lng = Number(point && point.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
      if (state && typeof state.containsLatLng === 'function') {
        return state.containsLatLng(lat, lng) === true
      }
      const footprint = Array.isArray(state && state.footprintLatLngs) ? state.footprintLatLngs : []
      if (footprint.length >= 3 && typeof window !== 'undefined' && typeof window.PORTAL_GEO_TIFF_POINT_IN_POLYGON === 'function') {
        return window.PORTAL_GEO_TIFF_POINT_IN_POLYGON(lat, lng, footprint) === true
      }
      return true
    }
    if (!fnHasLocalTerrainSampler(localTerrainState)) {
      throw new Error(fnT('dem.unknown_provider'))
    }
    let out = []
    const outProviders = pointsPayload.map(function () { return '' })
    const insideFlags = pointsPayload.map(function (pt) {
      return fnIsPointInsideLocalTerrain(localTerrainState, pt)
    })
    try {
      if (typeof localTerrainState.sampleElevations === 'function') {
        const requestIndexMap = []
        const requestPoints = pointsPayload.filter(function (pt, idx) {
          const inside = insideFlags[idx] === true
          if (inside) requestIndexMap.push(idx)
          return inside
        })
        const sampledValues = requestPoints.length
          ? await localTerrainState.sampleElevations(requestPoints, { resampling: 'nearest' })
          : []
        out = pointsPayload.map(function (_pt, idx) {
          const sampledIdx = requestIndexMap.indexOf(idx)
          if (sampledIdx < 0) return null
          const sampledRaw = sampledValues[sampledIdx]
          if (sampledRaw === null || sampledRaw === undefined) return null
          const sampled = Number(sampledRaw)
          return Number.isFinite(sampled) ? sampled : null
        })
      } else if (typeof localTerrainState.sampleElevationAsync === 'function') {
        const sampledValues = await Promise.all(pointsPayload.map(async function (pt) {
          const lat = Number(pt && pt.lat)
          const lng = Number(pt && pt.lng)
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
          if (!fnIsPointInsideLocalTerrain(localTerrainState, pt)) return null
          const sampledRaw = await localTerrainState.sampleElevationAsync(lat, lng, { resampling: 'nearest' })
          if (sampledRaw === null || sampledRaw === undefined) return null
          const sampled = Number(sampledRaw)
          return Number.isFinite(sampled) ? sampled : null
        }))
        out = sampledValues
      } else {
        out = pointsPayload.map(function (pt) {
          const lat = Number(pt && pt.lat)
          const lng = Number(pt && pt.lng)
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
          if (!fnIsPointInsideLocalTerrain(localTerrainState, pt)) return null
          const sampledRaw = localTerrainState.sampleElevation(lat, lng)
          if (sampledRaw === null || sampledRaw === undefined) return null
          const sampled = Number(sampledRaw)
          return Number.isFinite(sampled) ? sampled : null
        })
      }
    } catch (_error) {
      out = pointsPayload.map(function () { return null })
    }
    out.forEach(function (val, idx) {
      if (fnToFiniteElevationOrNull(val) === null) return
      outProviders[idx] = normalizedProviderKey
    })
    const missingIndexes = []
    out.forEach(function (val, idx) {
      if (fnToFiniteElevationOrNull(val) === null) missingIndexes.push(idx)
    })
    logHeightToolDebug('local_dem_sample', {
      providerKey: normalizedProviderKey,
      fallbackProviderKey: localFallbackProviderKey,
      points: pointsPayload.map(function (pt, idx) {
        return {
          idx: idx,
          lat: pt && pt.lat,
          lng: pt && pt.lng,
          inside: insideFlags[idx] === true,
          elevation: fnToFiniteElevationOrNull(out[idx]),
          sourceProvider: String(outProviders[idx] || '').trim()
        }
      }),
      missingIndexes: missingIndexes.slice()
    })
    if (!missingIndexes.length) return fnBuildElevationResultRows(out, outProviders, normalizedProviderKey)
    if (!localFallbackProviderKey || localFallbackProviderKey === LOCAL_DEM_PROVIDER_KEY) return fnBuildElevationResultRows(out, outProviders, normalizedProviderKey)
    if (typeof fetchImpl !== 'function') return fnBuildElevationResultRows(out, outProviders, normalizedProviderKey)

    const fallbackPoints = missingIndexes.map(function (idx) { return pointsPayload[idx] })
    logHeightToolDebug('local_dem_fallback_request', {
      providerKey: normalizedProviderKey,
      fallbackProviderKey: localFallbackProviderKey,
      missingIndexes: missingIndexes.slice(),
      points: fallbackPoints.map(function (pt, idx) {
        return {
          idx: missingIndexes[idx],
          lat: pt && pt.lat,
          lng: pt && pt.lng
        }
      })
    })
    const fallbackResponse = await fetchImpl('/api/dem-elevation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: localFallbackProviderKey,
        points: fallbackPoints
      })
    })
    const fallbackPayload = await fallbackResponse.json().catch(function () { return null })
    if (fallbackResponse.ok) {
      const fallbackResults = Array.isArray(fallbackPayload && fallbackPayload.results) ? fallbackPayload.results : []
      logHeightToolDebug('local_dem_fallback_response', {
        providerKey: normalizedProviderKey,
        fallbackProviderKey: localFallbackProviderKey,
        ok: true,
        results: fallbackResults.map(function (row, idx) {
          return {
            idx: missingIndexes[idx],
            elevation: fnToFiniteElevationOrNull(row && row.elevation)
          }
        })
      })
      missingIndexes.forEach(function (originalIdx, idx) {
        const row = fallbackResults[idx]
        const elevation = fnToFiniteElevationOrNull(row && row.elevation)
        if (elevation === null) return
        out[originalIdx] = elevation
        outProviders[originalIdx] = localFallbackProviderKey
      })
      logHeightToolDebug('local_dem_fallback_merged', {
        providerKey: normalizedProviderKey,
        fallbackProviderKey: localFallbackProviderKey,
        points: pointsPayload.map(function (pt, idx) {
          return {
            idx: idx,
            lat: pt && pt.lat,
            lng: pt && pt.lng,
            inside: insideFlags[idx] === true,
            elevation: fnToFiniteElevationOrNull(out[idx]),
            sourceProvider: String(outProviders[idx] || '').trim()
          }
        })
      })
    } else {
      logHeightToolDebug('local_dem_fallback_response', {
        providerKey: normalizedProviderKey,
        fallbackProviderKey: localFallbackProviderKey,
        ok: false,
        status: fallbackResponse.status,
        payload: fallbackPayload
      })
    }
    return fnBuildElevationResultRows(out, outProviders, normalizedProviderKey)
  }

  if (!fnIsDemProviderEnabled(normalizedProviderKey)) {
    throw new Error(fnT('dem.unknown_provider'))
  }
  if (typeof fetchImpl !== 'function') {
    throw new Error(fnT('dem.request_failed'))
  }

  const response = await fetchImpl('/api/dem-elevation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      provider: normalizedProviderKey,
      points: pointsPayload
    })
  })
  const payload = await response.json().catch(function () { return null })
  if (!response.ok) {
    const message = String((payload && payload.error) || ('DEM proxy hiba: HTTP ' + response.status))
    throw new Error(message)
  }
  const results = Array.isArray(payload && payload.results) ? payload.results : []
  const out = batchPoints.map(function (_pt, idx) {
    const row = results[idx]
    const rawElevation = row && Object.prototype.hasOwnProperty.call(row, 'elevation') ? row.elevation : null
    if (rawElevation === null || rawElevation === undefined) return null
    if (typeof rawElevation === 'string' && !String(rawElevation).trim()) return null
    const elevation = Number(rawElevation)
    if (!Number.isFinite(elevation)) return null
    return elevation
  })
  return fnBuildElevationResultRows(out, null, normalizedProviderKey)
}

const createDemBatchController = function ({
  _ = window._,
  $ = window.jQuery,
  Swal = window.Swal,
  areaSwal = Swal,
  fnT = function (key) { return String(key || '') },
  fnTF = function (key) { return String(key || '') },
  POINT_ALTITUDE_MODE_VALUES = ['absolute', 'relativeToStartPoint'],
  DEM_PROVIDER_CONFIG = {},
  DEM_PROVIDER_PRIORITY = [],
  fnIsDemProviderEnabled = function () { return true },
  fnGetMissionState = function () { return null },
  fnBuildDemLoadingPopupHtml = function () { return '' },
  fnCaptureState = function () { return null },
  fnGetDemProviderLabel = function (providerKey) { return String(providerKey || '') },
  fnFetchSurfaceElevationByProvider = async function () { return [] },
  fnSetPointDemValue = function () {},
  fnBuildPointCoordinatesText = function () { return '' },
  fnTemplateSupportsPointCoordinatesField = function () { return false },
  fnSetByPath = function () {},
  fnUpdatePointXmlByPath = function () {},
  fnSetPointAglValue = function () {},
  fnSetPointDescription = function () {},
  fnBuildAglLine = function () { return '' },
  fnBuildDemLine = function () { return '' },
  fnGetWaypointTooltipText = function () { return '' },
  fnUpdateMissionLineCoords = function () {},
  fnScheduleRoute3dSharedSync = function () {},
  fnHistoryPush = function () {},
} = {}) {
  const fnGetLocalTerrainState = function () {
    if (typeof window === 'undefined') return null
    return window[LOCAL_TERRAIN_STATE_KEY] || null
  }
  const fnIsLocalProviderActive = function () {
    const state = fnGetLocalTerrainState()
    return !!(
      state &&
      state.active === true &&
      (
        typeof state.sampleElevation === 'function' ||
        typeof state.sampleElevationAsync === 'function' ||
        typeof state.sampleElevations === 'function'
      )
    )
  }
  const fnIsProviderAvailable = function (providerKey) {
    const key = String(providerKey || '').trim()
    if (!key) return false
    if (key === LOCAL_DEM_PROVIDER_KEY) return fnIsLocalProviderActive()
    return fnIsDemProviderEnabled(key)
  }
  const fnGetProviderBatchSize = function (providerKey) {
    const key = String(providerKey || '').trim()
    if (key === LOCAL_DEM_PROVIDER_KEY) return 200
    return Math.max(1, parseInt(((DEM_PROVIDER_CONFIG[key] || {}).batchSize), 10) || 50)
  }

  const applyProviderToMissionPoints = async function (providerKey, options) {
    const mergedOpts = (_.assign || Object.assign)({
      showResultModal: true,
      mode: 'dem',
      altitudeOffsetMeters: 0,
      aglAltitudeMode: 'absolute'
    }, options || {})
    const mode = String(mergedOpts.mode || 'dem').trim().toLowerCase() === 'agl' ? 'agl' : 'dem'
    const altitudeOffsetMeters = Number(mergedOpts.altitudeOffsetMeters)
    const aglAltitudeModeRaw = String(mergedOpts.aglAltitudeMode || 'absolute').trim()
    const aglAltitudeMode = POINT_ALTITUDE_MODE_VALUES.includes(aglAltitudeModeRaw) ? aglAltitudeModeRaw : 'absolute'
    if (mode === 'agl' && !Number.isFinite(altitudeOffsetMeters)) {
      if (mergedOpts.showResultModal) Swal.fire(fnT('dem.invalid_altitude'))
      return { ok: false, provider: providerKey, error: fnT('dem.invalid_altitude') }
    }
    if (mode === 'agl' && altitudeOffsetMeters < 1) {
      if (mergedOpts.showResultModal) Swal.fire(fnT('dem.altitude_min_agl'))
      return { ok: false, provider: providerKey, error: fnT('dem.altitude_min_agl') }
    }

    const missionState = fnGetMissionState()
    if (!missionState || !Array.isArray(missionState.waypoints) || !missionState.waypoints.length) {
      if (mergedOpts.showResultModal) Swal.fire(fnT('dem.no_points'))
      return { ok: false, provider: providerKey, error: fnT('dem.no_points') }
    }
    if (!fnIsProviderAvailable(providerKey)) {
      if (mergedOpts.showResultModal) Swal.fire(fnT('dem.unknown_provider'))
      return { ok: false, provider: providerKey, error: fnT('dem.unknown_provider') }
    }
    const points = missionState.waypoints.filter(function (pt) {
      const lat = parseFloat(pt && pt.latlng && pt.latlng[0])
      const lng = parseFloat(pt && pt.latlng && pt.latlng[1])
      return Number.isFinite(lat) && Number.isFinite(lng)
    })
    if (!points.length) {
      if (mergedOpts.showResultModal) Swal.fire(fnT('dem.no_valid_coordinates'))
      return { ok: false, provider: providerKey, error: fnT('dem.no_valid_coordinates') }
    }
    const historyBefore = fnCaptureState()
    let changed = false
    const batchSize = fnGetProviderBatchSize(providerKey)
    const providerLabel = fnGetDemProviderLabel(providerKey)
    let runResult = { ok: false, provider: providerKey, changed: false }
    const loadingTask = startPortalLoadingTask({
      title: (mode === 'agl' ? fnT('dem.agl_prefix') : fnT('dem.surface_prefix')) + providerLabel,
      status: fnT('dem.popup.starting'),
      progress: 0,
      mode: 'determinate'
    })
    try {
      const total = points.length
      let processed = 0
      const missionAltContext = getMissionAltitudeContextFor3d(missionState, buildDemBatchAltitudeSyncContext(missionState, {
        DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
        fnIsDemProviderEnabled: fnIsDemProviderEnabled,
        fnSetByPath: fnSetByPath,
        fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
        fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
        fnBuildPointCoordinatesText: fnBuildPointCoordinatesText
      }))
      loadingTask.update({
        progress: 0,
        status: providerKey + ' lekeres indul...'
      })
      for (let i = 0; i < total; i += batchSize) {
        const batch = points.slice(i, i + batchSize)
        const elevations = await fnFetchSurfaceElevationByProvider(providerKey, batch)
        batch.forEach(function (pt, idx) {
          const elevation = elevations[idx]
          if (!Number.isFinite(elevation)) return
          const rounded = Math.round(elevation * 10) / 10
          fnSetPointDemValue(pt, providerKey, rounded)
          if (mode === 'agl') {
            const aglMeters = Math.round((rounded + altitudeOffsetMeters) * 10) / 10
            pt.altitude = aglMeters
            if (!pt.props || typeof pt.props !== 'object') pt.props = {}
            if (!pt.props.coordinates || typeof pt.props.coordinates !== 'object') pt.props.coordinates = {}
            pt.props.coordinates.alt = aglMeters
            const pointCoordinatesText = fnBuildPointCoordinatesText(pt.props || {})
            if (fnTemplateSupportsPointCoordinatesField()) {
              fnSetByPath(pt.props, 'Point.coordinates', pointCoordinatesText)
            }
            fnSetByPath(pt.props, 'Point.altitudeMode', aglAltitudeMode)
            fnUpdatePointXmlByPath(pt, 'coordinates.alt', aglMeters)
            if (fnTemplateSupportsPointCoordinatesField()) {
              fnUpdatePointXmlByPath(pt, 'Point.coordinates', pointCoordinatesText)
            }
            fnUpdatePointXmlByPath(pt, 'Point.altitudeMode', aglAltitudeMode)
            syncPointAltitudeFields(pt, {
              sourceMissionAltContext: missionAltContext,
              targetMissionAltContext: missionAltContext,
              absoluteAltitudeOverride: aglMeters
            }, buildDemBatchAltitudeSyncContext(missionState, {
              DEM_PROVIDER_PRIORITY: DEM_PROVIDER_PRIORITY,
              fnIsDemProviderEnabled: fnIsDemProviderEnabled,
              fnSetByPath: fnSetByPath,
              fnUpdatePointXmlByPath: fnUpdatePointXmlByPath,
              fnTemplateSupportsPointCoordinatesField: fnTemplateSupportsPointCoordinatesField,
              fnBuildPointCoordinatesText: fnBuildPointCoordinatesText
            }))
            fnSetPointAglValue(pt, providerKey, altitudeOffsetMeters, aglMeters)
            fnSetPointDescription(pt, fnBuildAglLine(providerKey, rounded, altitudeOffsetMeters, aglMeters))
          } else {
            fnSetPointDescription(pt, fnBuildDemLine(providerKey, rounded))
          }
          if (pt.marker && pt.marker.setTooltipContent) {
            pt.marker.setTooltipContent(fnGetWaypointTooltipText(pt))
          }
          changed = true
        })
        processed += batch.length
        loadingTask.update({
          progress: total > 0 ? ((processed / total) * 100) : 100,
          status: providerKey + ' feldolgozva: ' + processed + ' / ' + total
        })
      }
      if (changed) {
        fnUpdateMissionLineCoords()
        fnScheduleRoute3dSharedSync()
        fnHistoryPush(historyBefore)
      }
      loadingTask.complete({
        status: providerKey + ' sikeres',
        progress: 100
      })
      runResult = { ok: true, provider: providerKey, changed: changed, message: providerKey + ' sikeres' }
      if (mergedOpts.showResultModal) {
        if (changed) {
          if (mode === 'agl') {
            Swal.fire(fnT('common.done'), fnTF('dem.refresh_ok_agl', { provider: providerKey }), 'success')
          } else {
            Swal.fire(fnT('common.done'), fnTF('dem.refresh_ok_desc', { provider: providerKey }), 'success')
          }
        } else {
          Swal.fire(fnT('common.no_change'), fnTF('dem.no_change', { provider: providerKey }), 'info')
        }
      }
    } catch (error) {
      console.error(fnT('log.dem_elevation_request_error'), error)
      const errText = String((error && error.message) || fnT('dem.request_failed'))
      loadingTask.fail({
        status: errText
      })
      runResult = { ok: false, provider: providerKey, error: errText }
      if (mergedOpts.showResultModal) {
        Swal.fire(fnT('common.error'), errText, 'error')
      }
    }
    return runResult
  }

  const applyAllProvidersToMissionPoints = async function () {
    const providers = (fnIsLocalProviderActive() ? [LOCAL_DEM_PROVIDER_KEY] : []).concat(DEM_PROVIDER_PRIORITY.slice())
    const summary = []
    for (let i = 0; i < providers.length; i++) {
      const providerKey = providers[i]
      const result = await applyProviderToMissionPoints(providerKey, { showResultModal: false })
      summary.push(result || { ok: false, provider: providerKey, error: fnT('common.unknown_error') })
    }
    const okRows = summary.filter(function (row) { return row && row.ok })
    const failRows = summary.filter(function (row) { return !row || !row.ok })
    const lines = []
    lines.push(fnTF('dem.summary_success_count', { ok: okRows.length, total: summary.length }))
    okRows.forEach(function (row) {
      lines.push(fnTF('dem.summary_ok_row', { provider: row.provider, label: fnGetDemProviderLabel(row.provider) }))
    })
    if (failRows.length) {
      lines.push('')
      lines.push(fnTF('dem.summary_failed_count', { failed: failRows.length }))
      failRows.forEach(function (row) {
        const reason = String((row && row.error) || fnT('common.unknown_error'))
        const provider = row && row.provider ? row.provider : fnT('common.unknown')
        lines.push(fnTF('dem.summary_error_row', { provider: provider, label: fnGetDemProviderLabel(provider), reason: reason }))
      })
    }
    const safeHtml = lines.map(function (line) {
      return String(line || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    }).join('<br>')
    if (failRows.length) {
      Swal.fire({
        title: fnT('dem.summary_title'),
        html: safeHtml,
        icon: 'warning'
      })
    } else {
      Swal.fire({
        title: fnT('dem.summary_title'),
        html: safeHtml,
        icon: 'success'
      })
    }
  }

  return {
    applyProviderToMissionPoints: applyProviderToMissionPoints,
    applyAllProvidersToMissionPoints: applyAllProvidersToMissionPoints
  }
}

export {
  createDemBatchController,
  fetchSurfaceElevationByProvider
}
